import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Flex, List, Divider, Typography, Modal } from "antd";
import { debounce } from "lodash";
import { useNavigate } from "react-router";
import CustomAvatar from "../../../Components/CustomAvatar";
import { LockIcon } from "../../../assets/icons";
import ApiService from "../../../API/apiService";
import { API_ENDPOINTS } from "../../../API/endpoints";

const { Text } = Typography;

const FindChannelModal = ({ userSelfData, onModalUpdate, channelList, chatList }) => {
  const navigate = useNavigate();
  const inputRef = useRef();
  
  const [searchChannelTerm, setSearchChannelTerm] = useState("");
  const [hoverState, setHoverState] = useState("");
  const [searchList, setSearchList] = useState({
    usersList: chatList || [],
    channelsList: channelList || [],
  });

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const searchChannelApi = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchList({
        usersList: chatList || [],
        channelsList: channelList || [],
      });
      return;
    }

    try {
      const response = await ApiService.post(API_ENDPOINTS.searchChannelApi, {
        searchTerm,
        userId: userSelfData?._id,
      });

      if (response?.status === 1) {
        const { users = [], channels = [] } = response.data;
        const firstItemId = users[0]?.userId || channels[0]?._id || "";
        
        setHoverState(firstItemId);
        setSearchList({ usersList: users, channelsList: channels });
      } else {
        setSearchList({ usersList: [], channelsList: [] });
      }
    } catch (error) {
      setSearchList({ usersList: [], channelsList: [] });
    }
  }, [userSelfData?._id, chatList, channelList]);

  const debouncedSearchChannelApi = useMemo(
    () => debounce(searchChannelApi, 600),
    [searchChannelApi]
  );

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchChannelTerm(value);
    debouncedSearchChannelApi(value);
  }, [debouncedSearchChannelApi]);

  const closeModal = useCallback(() => {
    onModalUpdate({ activeModal: "" });
    setSearchChannelTerm("");
  }, [onModalUpdate]);

  const navigateAndClose = useCallback((path, id) => {
    localStorage.setItem("current_id", id);
    navigate(path);
    closeModal();
  }, [navigate, closeModal]);

  const handleChatClick = useCallback((item) => {
    const id = item?._id || item?.userId;
    navigateAndClose(`/elsnertech/message/${id}`, id);
  }, [navigateAndClose]);

  const handleChannelClick = useCallback((item) => {
    navigateAndClose(`/elsnertech/channels/${item._id}`, item._id);
  }, [navigateAndClose]);

  const renderListItem = useCallback((item, onClick, isChannel = false) => (
    <List.Item
      onMouseEnter={() => setHoverState(item._id || item.userId)}
      className={hoverState === (item._id || item.userId) ? "hoverClass" : ""}
      onClick={() => onClick(item)}
    >
      <List.Item.Meta
        avatar={
          isChannel ? (
            item?.isPrivate ? <LockIcon /> : <i className="fa-solid fa-globe"></i>
          ) : (
            <CustomAvatar
              img={item?.thumbnail_avatarUrl}
              size={24}
              userName={item?.username}
            />
          )
        }
        title={isChannel ? item.name : item.username}
      />
    </List.Item>
  ), [hoverState]);

  return (
    <Modal
      destroyOnHidden
      title="Start Chat"
      open={true}
      onCancel={closeModal}
      footer={null}
      className="find-channel-modal"
      width={600}
      style={{ top: 20 }}
    >
      <Flex vertical className="ant-modal-subheader">
        <div className="search-container">
          <i className="fa-solid fa-magnifying-glass search-icon"></i>
          <input
            ref={inputRef}
            value={searchChannelTerm}
            onChange={handleSearchChange}
            type="text"
            className="custom-search"
            placeholder="Search"
          />
        </div>
      </Flex>
      
      <Divider />
      
      <Flex vertical className="suggestion-list-container">
        <Text type="secondary" className="title">Users</Text>
        <List
          className="suggestion-list-users"
          itemLayout="horizontal"
          dataSource={searchList.usersList}
          renderItem={(item) => renderListItem(item, handleChatClick)}
        />
      </Flex>
      
      <Divider />
      
      <Flex vertical className="suggestion-list-container">
        <Text type="secondary" className="title">Channels</Text>
        <List
          className="suggestion-list-users"
          itemLayout="horizontal"
          dataSource={searchList.channelsList}
          renderItem={(item) => renderListItem(item, handleChannelClick, true)}
        />
      </Flex>
    </Modal>
  );
};

export default FindChannelModal;